import axios from 'axios';
import {
  ICardIdData,
  ICustomerData,
  IServiceMontageData,
  ISubscriptionData,
  ISuitcaseMontageData,
  IUserProfileInfo,
} from '../interfaces';

const usersBaseUrl = process.env.REACT_APP_USERS_BASE_URL;
const checkoutBaseUrl = process.env.REACT_APP_CHECKOUT_BASE_URL;
const adminBaseUrl = process.env.REACT_APP_ADMIN_BASE_URL;

async function getUserProfileInfo(token: string): Promise<IUserProfileInfo> {
  const response = await axios.get(`${usersBaseUrl}profile/edit/user-info`, {
    headers: { authorization: token },
  });
  return response.data;
}

async function createBasicSubscriptionPlan(
  subscriptionData: ISubscriptionData,
  billingId: number,
  token: string
): Promise<string> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkoutPlans/basic-subscription-plan`,
    { subscriptionData, billingId },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function createCustomerAndCardId(
  customerData: ICustomerData,
  cardToken: string,
  token: string
): Promise<string> {
  const response = await axios.post(
    `${checkoutBaseUrl}billing/customer-and-card-id`,
    { customerData, cardToken },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function getCardsIds(token: string): Promise<ICardIdData[]> {
  const response = await axios.get(`${checkoutBaseUrl}billing/user-billings`, {
    headers: { authorization: token },
  });

  return response.data;
}

async function deleteCardId(billingId: number, token: string): Promise<void> {
  await axios.delete(`${checkoutBaseUrl}billing/user-billing/${billingId}`, {
    headers: { authorization: token },
  });
}

async function createCreditCardOrder(
  serviceMontageData: IServiceMontageData,
  billingId: number,
  token: string
): Promise<string> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkoutOutfitsMontage/credit-card-order`,
    { serviceMontageData, billingId },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function createPixOrder(
  serviceMontageData: IServiceMontageData,
  token: string
): Promise<{ QR_CODE: string; transactionId: string }> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkoutOutfitsMontage/pix-order`,
    { serviceMontageData },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function checkPaymentStatus(
  transactionId: string,
  token: string
): Promise<{ transactionPaid: boolean }> {
  const response = await axios.get(
    `${checkoutBaseUrl}checkoutOutfitsMontage/check-payment-status/${transactionId}`,
    {
      headers: { authorization: token },
    }
  );

  return response.data;
}

async function createCreditCardProductOrder(
  productData: any,
  billingId: number,
  token: string
): Promise<void> {
  await axios.post(
    `${checkoutBaseUrl}checkout/product/credit-card-order`,
    { productData, billingId },
    {
      headers: { authorization: token },
    }
  );
}

async function createPixProductOrder(productData: any, token: string): Promise<any> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkout/product/pix-order`,
    { productData },
    {
      headers: { authorization: token },
    }
  );

  return response.data;
}

async function checkProductPaymentStatus(
  transactionId: string,
  token: string
): Promise<{ transactionPaid: boolean }> {
  const response = await axios.get(
    `${checkoutBaseUrl}checkout/product/payment-status/${transactionId}`,
    {
      headers: { authorization: token },
    }
  );

  return response.data;
}

async function checkUserHasDiscount(token: string): Promise<{ transactionPaid: boolean }> {
  const response = await axios.get(`${checkoutBaseUrl}checkoutPlans/check-user-has-discount`, {
    headers: { authorization: token },
  });

  return response.data;
}

async function getUserSubInfo(token: string): Promise<{
  isActive: number;
  planName: string | null;
  planId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}> {
  const { data } = await axios.get(`${usersBaseUrl}check-subscriptions/info`, {
    headers: {
      authorization: token,
    },
  });

  return data;
}

async function createSuitcaseCreditCardOrder(
  serviceMontageData: ISuitcaseMontageData,
  billingId: number,
  token: string
): Promise<string> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkout-suitcase-montage/credit-card-order`,
    { serviceMontageData, billingId },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function createSuitcasePixOrder(
  serviceMontageData: ISuitcaseMontageData,
  token: string
): Promise<{ QR_CODE: string; transactionId: string }> {
  const response = await axios.post(
    `${checkoutBaseUrl}checkout-suitcase-montage/pix-order`,
    { serviceMontageData },
    {
      headers: { authorization: token },
    }
  );
  return response.data;
}

async function checkSuitcasePaymentStatus(
  transactionId: string,
  token: string
): Promise<{ transactionPaid: boolean }> {
  const response = await axios.get(
    `${checkoutBaseUrl}checkout-suitcase-montage/check-payment-status/${transactionId}`,
    {
      headers: { authorization: token },
    }
  );

  return response.data;
}

async function createLogCustomerData(step: string, log: any, token: string): Promise<void> {
  console.log('AQUIIIIIIII');
  await axios.post(
    `${checkoutBaseUrl}log/customer-data`,
    { step, log },
    {
      headers: { authorization: token },
    }
  );
}

async function verifyCoupon(
  code: string,
  productName: string,
  token: string
): Promise<{ couponId: number; discountValue: number }> {
  const response = await axios.get(
    `${adminBaseUrl}coupon/resolva-club/verify-coupon/${code}/${productName}`,
    {
      headers: {
        authorization: token,
      },
    }
  );

  return response.data;
}

export const apiCheckout = {
  getUserProfileInfo,
  createBasicSubscriptionPlan,
  createCustomerAndCardId,
  getCardsIds,
  deleteCardId,
  createCreditCardOrder,
  createPixOrder,
  checkPaymentStatus,
  createCreditCardProductOrder,
  createPixProductOrder,
  checkProductPaymentStatus,
  checkUserHasDiscount,
  createSuitcaseCreditCardOrder,
  createSuitcasePixOrder,
  checkSuitcasePaymentStatus,
  createLogCustomerData,
  verifyCoupon,
  getUserSubInfo,
};
